import { Component, Input, Output, EventEmitter, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../../../../core/services/theme.service';
import { IconComponent, IconName } from '../../../icons/icon.component';
import { IconComponentV2 } from '../../icon-component/icon.component';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'default' | 'defaultBorder' | 'custom';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule, IconComponent, IconComponentV2],
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: []
})
export class ButtonComponent {
  private themeService = inject(ThemeService);

  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() className: string = '';
  @Input() disabled: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() iconName?: IconName
  @Input() iconNameV2?: string;
  @Input() rounded: string = 'rounded-lg';
  @Input() maxHeight?: string;

  @Output() buttonClick = new EventEmitter<void>();

  isDark = computed(() => this.themeService.theme() === 'dark');

  private readonly variantClasses = computed(() => {
    const dark = this.isDark();
    return {
      primary: dark
        ? 'bg-gradient-to-br from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/30'
        : 'bg-gradient-to-br from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-purple-500/30',
      secondary: dark
        ? 'bg-slate-800/60 hover:bg-slate-700/60 text-purple-300 border border-purple-500/30'
        : 'bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-300',
      outline: dark
        ? 'bg-transparent hover:bg-purple-500/10 text-purple-300 border border-purple-500/50 hover:border-purple-500'
        : 'bg-transparent hover:bg-purple-50 text-purple-600 border border-purple-400 hover:border-purple-500',
      ghost: dark
        ? 'bg-transparent hover:bg-slate-800/50 text-slate-300 hover:text-purple-300'
        : 'bg-transparent hover:bg-purple-100 text-slate-600 hover:text-purple-600',
      danger: dark
        ? 'bg-gradient-to-br from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white shadow-lg shadow-red-500/30'
        : 'bg-gradient-to-br from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg shadow-red-500/30',
      default: 'bg-[#AD46FF] text-white hover:bg-[#AD46FF]/80',
      defaultBorder: 'bg-transparent text-[#AD46FF] border border-[#AD46FF]',
      custom: 'border border-[#AD46FF]',
    };
  });

  private readonly sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3',
  };

  getButtonClasses(): string {
    const baseClasses = `${this.rounded} transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium`;
    const variantClass = this.variantClasses()[this.variant];
    const sizeClass = this.sizeClasses[this.size];

    return `${baseClasses} ${variantClass} ${sizeClass} ${this.className}`;
  }

  handleClick(): void {
    if (!this.disabled) {
      this.buttonClick.emit();
    }
  }
}
